<template>
  <v-dialog max-width="600px" persistent v-model="showUploadDialog">
    <v-card dark v-if="allowed" class="mx-auto pa-4" outlined>
      <input type="file" ref="fileInput" @change="handleFileSelect" style="display: none" />
      <div v-if="!file" class="drop-area" @dragover.prevent @dragenter.prevent @drop.prevent="handleDrop" @click="triggerFileInput">
        <v-icon size="48" color="primary">mdi-cloud-upload</v-icon>
        <div>Datei per Drag & Drop hier ablegen oder klicken zum Auswählen</div>
      </div>

      <div v-if="file">
        <v-alert type="info" dense>Datei ausgewählt: {{ file.name }}</v-alert>

        <v-checkbox
          v-if="userService.hasRight('d_fetchAllDocs')"
          v-model="assignAndSign"
          label="Dokumentendetails direkt erfassen und zuweisen"></v-checkbox>

        <div v-if="assignAndSign">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="documentData.title" label="Titel"></v-text-field>
              <v-textarea v-model="documentData.description" label="Beschreibung"></v-textarea>
              <v-autocomplete v-model="documentData.studio" :items="mappedStudios" label="Studio" item-text="name" item-value="_id" clearable>
              </v-autocomplete>
              <v-autocomplete
                return-object
                v-model="selectedInvoiceIssuer"
                :items="invoiceIssuerOptions"
                label="Rechnungssteller"
                item-text="title"
                item-value="_id"
                clearable
                :search-input.sync="issuerSearch"
                @keydown.enter.prevent>
                <template v-slot:no-data>
                  <v-list-item @click="createNewInvoiceIssuer(issuerSearch)" style="cursor: pointer">
                    <v-list-item-content>
                      <v-list-item-title>Rechnungssteller "{{ issuerSearch }}" hinzufügen</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="documentData.amount" label="Betrag" type="number" prepend-icon="mdi-cash"></v-text-field>
              <v-text-field v-model="documentData.invoiceNumber" label="Rechnungsnummer"></v-text-field>
              <v-menu ref="menu" v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="documentData.date" label="Datum" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on">
                  </v-text-field>
                </template>
                <v-date-picker v-model="documentData.date" @input="dateMenu = false"></v-date-picker>
              </v-menu>
              <user-search :value="selectedUser" @update="selectedUser = $event" label="Benutzer zuweisen" ignoreRights instructor></user-search>
              <v-checkbox v-model="signImmediately" label="Dokument sofort als visiert markieren"></v-checkbox>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-alert v-if="uploadSuccess" type="success" dismissible>Datei erfolgreich hochgeladen!</v-alert>
      <v-alert v-if="uploadError" type="error" dismissible>{{ uploadError }}</v-alert>

      <v-card-actions class="justify-center pt-5">
        <v-btn v-if="file" color="primary" @click="uploadAndProcessFile">Speichern</v-btn>
        <v-btn color="secondary" @click="hideUploadDialog">Abbrechen</v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else class="mx-auto pa-4">
      <v-alert type="info">Um ein Dokument einzureichen, sende einfach eine Mail an kreditoren@wellcomefit.ch</v-alert>
      <v-card-actions class="justify-center pt-5">
        <v-btn color="secondary" @click="hideUploadDialog">Alles klar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import { mapState } from "vuex"

export default {
  name: "FileDropUpload",
  components: { userSearch },
  props: {
    showUploadDialog: {
      type: Boolean,
      required: true,
    },
    allowed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploadSuccess: false,
      uploadError: "",
      file: null,
      assignAndSign: false,
      signImmediately: false,
      selectedUser: null,
      userService,
      documentData: {
        title: "",
        description: "",
        studio: null,
        amount: null,
        invoiceNumber: "",
        date: new Date().toISOString().substr(0, 10),
      },
      selectedInvoiceIssuer: null,
      invoiceIssuerOptions: [],
      issuerSearch: "",
      dateMenu: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    mappedStudios() {
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => ({ _id: studio._id, name: studio.name }))
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    // HINZUGEFÜGT: Verarbeitet die durch Klick ausgewählte Datei
    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) {
        this.file = files[0]
        this.documentData.title = this.file.name.replace(/\.[^/.]+$/, "")
      }
    },
    resetForm() {
      this.file = null
      this.assignAndSign = false
      this.signImmediately = false
      this.selectedUser = this.userService.getUser()._id
      this.documentData = {
        title: "",
        description: "",
        studio: null,
        amount: null,
        invoiceNumber: "",
        date: new Date().toISOString().substr(0, 10),
      }
      this.selectedInvoiceIssuer = null
      this.issuerSearch = ""
    },
    hideUploadDialog() {
      this.resetForm()
      this.uploadSuccess = false
      this.uploadError = ""
      this.$emit("hideDialog")
    },
    handleDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.file = files[0]
        this.documentData.title = this.file.name.replace(/\.[^/.]+$/, "") // Setzt Titel aus Dateinamen
      }
    },
    async fetchInvoiceIssuers() {
      try {
        this.invoiceIssuerOptions = await api.invoiceIssuers.fetch()
      } catch (err) {
        console.error("Fehler beim Abrufen der Rechnungssteller:", err)
      }
    },
    createNewInvoiceIssuer(title) {
      api.invoiceIssuers.create({ title }).then((createdIssuer) => {
        this.invoiceIssuerOptions.push(createdIssuer)
        this.selectedInvoiceIssuer = createdIssuer
        this.issuerSearch = ""
      })
    },
    async uploadAndProcessFile() {
      if (!this.file) return

      try {
        const formData = new FormData()
        formData.append("file", this.file)

        if (this.allowed && this.assignAndSign) {
          formData.append("assignedUserId", this.selectedUser)
          formData.append("sign", this.signImmediately)
          // Füge alle Dokumentendaten hinzu
          Object.keys(this.documentData).forEach((key) => {
            if (this.documentData[key] !== null) {
              formData.append(key, this.documentData[key])
            }
          })
          if (this.selectedInvoiceIssuer) {
            formData.append("invoiceIssuer", this.selectedInvoiceIssuer._id)
          }
        }

        await api.documents.uploadDocument(formData)
        this.uploadSuccess = true
        this.uploadError = ""
        this.$emit("fileUpload")
        this.hideUploadDialog()
      } catch (error) {
        this.uploadSuccess = false
        this.uploadError = "Fehler beim Hochladen der Datei."
        console.error("Upload error:", error)
      }
    },
  },
  watch: {
    showUploadDialog(newVal) {
      if (newVal) {
        this.resetForm()
        this.fetchInvoiceIssuers()
      }
    },
  },
}
</script>

<style scoped>
.drop-area {
  border: 2px dashed #1976d2;
  padding: 50px;
  text-align: center;
  cursor: pointer;
}
</style>
